import { type ReactNode, useState, useRef, useEffect, useCallback, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  getProductSpecs,
  isOutsideFlag,
  resolveSpecImage,
  type ProductSpecOption,
  type ProductWithSpecs,
} from '@/api/product';
import { formatPlanQuantity } from '@/api/order';

/**
 * 通用采购清单表格：允许同一规格多行（代购下单、购物车、订单编辑）。
 * 采购计划创建请使用 UniqueSpecOrderItemsTable（同规格自动合并）。
 */
export interface OrderItemSpecOption {
  id: string;
  name: string;
  price: number;
  unit: string;
  /** 是否清单外规格 */
  isOutside?: boolean;
}

export interface OrderItem {
  productId: string;
  name: string;
  quantity: number;
  unitPrice: number;
  unit: string;
  specId?: string;
  /** 接口返回的规格名称，优先展示 */
  spec?: string;
  image?: string;
  itemRemark?: string;
  /** 选中物资时缓存的规格列表，修改规格时直接使用 */
  specOptions?: OrderItemSpecOption[];
  /** 是否清单外（order_lists.isoutside） */
  isOutside?: boolean;
  /** 计划已使用量（当前规格单位，多单位可能含小数） */
  planUsedQuantity?: number | null;
  /** 计划量（当前规格单位） */
  planQuantity?: number | null;
  planBaseUsedQuantity?: number | null;
  planBaseQuantity?: number | null;
  planMultipleUnit?: number | null;
}

export interface ProductPickPayload {
  productId: string;
  name: string;
  image?: string;
  specId: string;
  spec: string;
  unitPrice: number;
  unit: string;
  specs: OrderItemSpecOption[];
  isOutside?: boolean;
}

interface OrderItemsTableProps {
  items: OrderItem[];
  editMode: boolean;
  onUpdateQuantity: (index: number, quantity: number) => void;
  onUpdateSpec: (index: number, specId: string, price: number, model: string, unit?: string) => void;
  onUpdateProduct: (index: number, product: ProductPickPayload) => void;
  onUpdateRemark: (index: number, remark: string) => void;
  onDeleteItem: (index: number) => void;
  onAddItem?: () => void;
  lastItemError?: boolean;
  /** 标题栏左侧内容（如科室/下单人筛选） */
  headerLeft?: ReactNode;
  headerActions?: ReactNode;
  /** 隐藏默认「采购清单」标题 */
  hideTitle?: boolean;
  /** 渲染在采购总额下方的扩展内容（如订单备注） */
  footerExtra?: ReactNode;
  /** 规格下拉是否提供「不限定规格」（采购计划按商品总量限购） */
  allowUnlimitedSpec?: boolean;
  /** 显示「计划已使用」列（只读，编辑模式也不可改） */
  showPlanUsage?: boolean;
  /** 显示「清单外」列（订单详情 order_lists.isoutside） */
  showOutsideColumn?: boolean;
  /** 显示「全院计划数」列（科室限购对照） */
  showHospitalPlan?: boolean;
  hospitalPlanQuantityOf?: (item: OrderItem) => number;
}

/** 不限定规格展示文案 */
export const UNLIMITED_SPEC_LABEL = '不限定规格';

export function isUnlimitedSpecItem(item: Pick<OrderItem, 'productId' | 'specId'>): boolean {
  return !!String(item.productId || '').trim() && !String(item.specId || '').trim();
}

const ITEM_REMARK_MAX = 200;

export function createEmptyOrderItem(): OrderItem {
  return {
    productId: '',
    name: '',
    quantity: 1,
    unitPrice: 0,
    unit: '件',
    specId: undefined,
    spec: '',
    image: '',
    itemRemark: '',
    specOptions: [],
  };
}

export function isEmptyOrderItem(item: OrderItem): boolean {
  return !item.productId && !item.specId && !String(item.name || '').trim();
}

/** 保证末尾有且仅有一行空行（避免堆积过多空行） */
export function ensureSingleTrailingEmpty(items: OrderItem[]): OrderItem[] {
  const next = [...items];
  while (next.length > 0 && isEmptyOrderItem(next[next.length - 1])) {
    next.pop();
  }
  next.push(createEmptyOrderItem());
  return next;
}

function mapSpecOption(spec: ProductSpecOption): OrderItemSpecOption {
  const price = Number(spec.price ?? 0);
  return {
    id: String(spec.id),
    name: spec.name || spec.sku || '未命名规格',
    price: Number.isFinite(price) ? price : 0,
    unit: spec.unit || '件',
    isOutside: isOutsideFlag(spec.is_outside),
  };
}

function pickSpec(row: ProductWithSpecs, spec: OrderItemSpecOption): ProductPickPayload {
  const specs = Array.isArray(row.specs) ? row.specs.map(mapSpecOption) : [spec];
  const raw = (row.specs || []).find((item) => String(item.id) === spec.id);
  return {
    productId: String(row.goods_id || row.goods?.id || ''),
    name: row.goods?.title || '未命名物资',
    image: resolveSpecImage(raw?.pic, row.goods?.m_pic),
    specId: spec.id,
    spec: spec.name,
    unitPrice: spec.price,
    unit: spec.unit,
    specs,
    isOutside: spec.isOutside === true,
  };
}

function useFixedDropdownPosition(open: boolean, anchorRef: React.RefObject<HTMLElement | null>) {
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });

  const update = useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
  }, [anchorRef]);

  useLayoutEffect(() => {
    if (!open) return;
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [open, update]);

  return pos;
}

// ──── Inline quantity editor ────
function QuantityCell({
  value,
  onChange,
  disabled,
}: {
  value: number;
  onChange: (v: number) => void;
  disabled?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(value));
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) {
      setDraft(String(value));
      setTimeout(() => inputRef.current?.select(), 0);
    }
  }, [editing, value]);

  const commit = () => {
    const v = parseInt(draft, 10);
    if (!Number.isNaN(v) && v >= 1 && v <= 9999) {
      onChange(v);
    } else {
      setDraft(String(value));
    }
    setEditing(false);
  };

  if (disabled) {
    return <span className="text-sm text-foreground-400">-</span>;
  }

  if (editing) {
    return (
      <input
        ref={inputRef}
        type="number"
        min={1}
        max={9999}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') {
            setDraft(String(value));
            setEditing(false);
          }
        }}
        className="w-16 px-1.5 py-0.5 text-sm text-center border border-primary-400 rounded-md outline-none focus:ring-1 focus:ring-primary-200 bg-white"
        autoFocus
      />
    );
  }

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md border border-dashed border-background-300 hover:border-primary-400 hover:bg-primary-50 transition-colors group cursor-pointer"
      title="点击修改数量"
    >
      <span className="text-sm text-foreground-800">{value}</span>
      <i className="ri-pencil-line text-[10px] text-foreground-400 group-hover:text-primary-500" />
    </button>
  );
}

// ──── 物资备注 ────
function RemarkCell({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
}) {
  if (disabled) {
    return <span className="text-xs text-foreground-400">-</span>;
  }

  const unmatched = value.startsWith('无法匹配');

  return (
    <input
      type="text"
      value={value}
      maxLength={ITEM_REMARK_MAX}
      onChange={(e) => onChange(e.target.value.slice(0, ITEM_REMARK_MAX))}
      placeholder="填写物资备注"
      title={`${value.length}/${ITEM_REMARK_MAX}`}
      className={`w-full min-w-[120px] px-2 py-1 text-xs border border-dashed rounded-md outline-none hover:border-primary-400 focus:border-primary-400 focus:ring-1 focus:ring-primary-200 bg-white placeholder:text-foreground-400 ${
        unmatched
          ? 'border-red-300 text-red-600'
          : 'border-background-300 text-foreground-800'
      }`}
    />
  );
}

// ──── Product name cell：搜索物资，按规格可选 ────
function ProductNameCell({
  name,
  currentSpecId,
  onPick,
}: {
  name: string;
  currentSpecId?: string;
  onPick: (payload: ProductPickPayload) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [list, setList] = useState<ProductWithSpecs[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const anchorRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const fetchGenRef = useRef(0);
  const keywordRef = useRef('');
  const pos = useFixedDropdownPosition(open, anchorRef);

  const fetchList = useCallback(async (q: string) => {
    const gen = ++fetchGenRef.current;
    setLoading(true);
    setError('');
    try {
      const res = await getProductSpecs({ search: q || undefined });
      // 忽略过期请求，避免慢返回覆盖当前列表
      if (gen !== fetchGenRef.current) return;
      const ok = res.error === false || Number(res.error) === 0;
      if (!ok) {
        setError(res.message || res.msg || '物资加载失败');
        setList([]);
        return;
      }
      setList(Array.isArray(res.data) ? res.data : []);
    } catch {
      if (gen !== fetchGenRef.current) return;
      setError('物资加载失败');
      setList([]);
    } finally {
      if (gen === fetchGenRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (anchorRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // 打开：重置搜索并拉取一次；关闭：作废进行中的请求
  useEffect(() => {
    if (!open) {
      fetchGenRef.current += 1;
      return;
    }
    setSearch('');
    keywordRef.current = '';
    setError('');
    void fetchList('');
    const timer = window.setTimeout(() => inputRef.current?.focus(), 50);
    return () => window.clearTimeout(timer);
  }, [open, fetchList]);

  // 搜索防抖：仅在关键词实际变化时请求
  useEffect(() => {
    if (!open) return;
    const timer = window.setTimeout(() => {
      const next = search.trim();
      if (next === keywordRef.current) return;
      keywordRef.current = next;
      void fetchList(next);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [search, open, fetchList]);

  const close = useCallback(() => {
    setOpen(false);
    setSearch('');
    keywordRef.current = '';
  }, []);

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1 max-w-full px-2 py-0.5 rounded-md border border-dashed border-background-300 hover:border-primary-400 hover:bg-primary-50 transition-colors group cursor-pointer text-left"
        title="点击修改物资"
      >
        <span className="text-sm font-medium text-foreground-900 truncate">
          {name || '选择物资'}
        </span>
        <i className="ri-pencil-line text-[10px] text-foreground-400 group-hover:text-primary-500 shrink-0" />
      </button>

      {open &&
        createPortal(
          <div
            ref={panelRef}
            style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 9999 }}
            className="bg-white border border-background-200 rounded-lg w-[360px] shadow-lg overflow-hidden"
          >
            <div className="p-2 border-b border-background-100">
              <div className="flex items-center gap-1.5 px-2 py-1.5 border border-background-300 rounded-md bg-background-50">
                <i className="ri-search-line text-foreground-400 text-xs" />
                <input
                  ref={inputRef}
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="搜索物资名称/编码/规格"
                  className="flex-1 text-xs bg-transparent outline-none"
                />
              </div>
            </div>
            <div className="max-h-72 overflow-y-auto">
              {loading && list.length === 0 ? (
                <div className="py-6 text-center text-xs text-foreground-400">
                  <i className="ri-loader-4-line animate-spin mr-1" />
                  加载中...
                </div>
              ) : error ? (
                <div className="py-6 text-center text-xs text-primary-600">{error}</div>
              ) : list.length === 0 ? (
                <div className="py-6 text-center text-xs text-foreground-400">未找到物资</div>
              ) : (
                list.map((row) => {
                  const title = row.goods?.title || '未命名物资';
                  const code = row.goods?.code || '';
                  const specs = Array.isArray(row.specs) ? row.specs.map(mapSpecOption) : [];
                  const thumb = resolveSpecImage(row.specs?.[0]?.pic, row.goods?.m_pic);
                  if (specs.length === 0) return null;
                  return (
                    <div key={String(row.id)} className="border-b border-background-100 last:border-b-0">
                      <div className="flex items-center gap-2 px-3 pt-2.5 pb-1.5 bg-background-50/80">
                        <div className="w-8 h-8 rounded overflow-hidden bg-background-100 shrink-0">
                          {thumb ? (
                            <img src={thumb} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <i className="ri-image-line text-foreground-300 text-sm" />
                            </div>
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-medium text-foreground-900 truncate">{title}</p>
                          <p className="text-[10px] text-foreground-500 truncate">
                            {code || '-'} · {specs.length} 个规格
                            {loading ? ' · 更新中...' : ''}
                          </p>
                        </div>
                      </div>
                      <div className="pb-1">
                        {specs.map((spec) => {
                          const isActive = currentSpecId != null && String(currentSpecId) === spec.id;
                          return (
                            <button
                              key={spec.id}
                              type="button"
                              onClick={() => {
                                onPick(pickSpec(row, spec));
                                close();
                              }}
                              className={`w-full text-left flex items-center gap-2 pl-12 pr-3 py-1.5 transition-colors ${
                                isActive
                                  ? 'bg-primary-50 text-primary-700'
                                  : 'text-foreground-700 hover:bg-background-50'
                              }`}
                              title="选择此规格"
                            >
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-1.5 min-w-0">
                                  <p className="text-xs font-medium truncate">{spec.name}</p>
                                  {spec.isOutside && (
                                    <span className="px-1.5 py-0.5 bg-orange-500 text-white text-[10px] rounded font-medium shrink-0">
                                      清单外
                                    </span>
                                  )}
                                </div>
                                <p className="text-[10px] text-foreground-500">
                                  {`¥${spec.price.toFixed(2)} / ${spec.unit}`}
                                </p>
                              </div>
                              {isActive && (
                                <i className="ri-check-line text-primary-500 shrink-0" />
                              )}
                              {!isActive && (
                                <i className="ri-add-line text-foreground-300 shrink-0" />
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}

// ──── Spec dropdown：优先使用行内缓存的 specs ────
export function SpecCell({
  productId,
  specId,
  currentModel,
  options,
  allowUnlimitedSpec = false,
  onChange,
}: {
  productId: string;
  specId?: string;
  currentModel: string;
  options?: OrderItemSpecOption[];
  allowUnlimitedSpec?: boolean;
  onChange: (specId: string, price: number, model: string, unit: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [fetchedSpecs, setFetchedSpecs] = useState<OrderItemSpecOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const anchorRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const pos = useFixedDropdownPosition(open, anchorRef);

  const cachedSpecs = Array.isArray(options) ? options : [];
  const hasCache = cachedSpecs.length > 0;
  const specs = hasCache ? cachedSpecs : fetchedSpecs;
  const unlimitedSelected = allowUnlimitedSpec && !String(specId || '').trim() && !!productId;
  const displayModel = unlimitedSelected
    ? UNLIMITED_SPEC_LABEL
    : currentModel && currentModel !== '-'
      ? currentModel
      : '选择规格';

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (anchorRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // 仅在没有缓存规格时才请求（例如详情带入的历史行）
  useEffect(() => {
    if (!open || !productId || hasCache) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const res = await getProductSpecs({ goods_id: Number(productId) || productId });
        if (cancelled) return;
        const ok = res.error === false || Number(res.error) === 0;
        if (!ok) {
          setError(res.message || res.msg || '规格加载失败');
          setFetchedSpecs([]);
          return;
        }
        const row =
          (res.data || []).find(
            (item) => String(item.goods_id || item.goods?.id) === String(productId),
          ) || (res.data || [])[0];
        const list = Array.isArray(row?.specs) ? row.specs.map(mapSpecOption) : [];
        setFetchedSpecs(list);
      } catch {
        if (!cancelled) {
          setError('规格加载失败');
          setFetchedSpecs([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, productId, hasCache]);

  const fallbackPrice = specs[0]?.price ?? 0;
  const fallbackUnit = specs[0]?.unit || '件';

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        disabled={!productId}
        className="inline-flex items-center gap-1 max-w-[180px] px-2 py-0.5 rounded-md border border-dashed border-background-300 hover:border-primary-400 hover:bg-primary-50 transition-colors group cursor-pointer text-left disabled:opacity-50 disabled:cursor-not-allowed"
        title="点击修改规格"
      >
        <span
          className={`text-xs truncate ${
            unlimitedSelected ? 'text-accent-700 font-medium' : 'text-foreground-700'
          }`}
        >
          {displayModel}
        </span>
        <i className="ri-arrow-down-s-line text-[10px] text-foreground-400 group-hover:text-primary-500 shrink-0" />
      </button>
      {open &&
        createPortal(
          <div
            ref={panelRef}
            style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 9999 }}
            className="bg-white border border-background-200 rounded-lg min-w-[220px] max-h-56 overflow-y-auto shadow-lg"
          >
            {allowUnlimitedSpec && (
              <button
                type="button"
                onClick={() => {
                  onChange('', fallbackPrice, UNLIMITED_SPEC_LABEL, fallbackUnit);
                  setOpen(false);
                }}
                className={`w-full text-left flex items-start gap-2 px-3 py-2 border-b border-background-100 transition-colors ${
                  unlimitedSelected
                    ? 'bg-accent-50 text-accent-800 hover:bg-accent-50'
                    : 'text-foreground-700 hover:bg-background-50'
                }`}
              >
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium">{UNLIMITED_SPEC_LABEL}</p>
                  <p className="text-[10px] text-foreground-500">按商品总量限购，不指定规格</p>
                </div>
                {unlimitedSelected && (
                  <i className="ri-check-line text-accent-600 ml-auto shrink-0" />
                )}
              </button>
            )}
            {loading ? (
              <div className="py-4 text-center text-xs text-foreground-400">
                <i className="ri-loader-4-line animate-spin mr-1" />
                加载规格...
              </div>
            ) : error ? (
              <div className="py-4 text-center text-xs text-primary-600">{error}</div>
            ) : specs.length === 0 ? (
              <div className="py-4 text-center text-xs text-foreground-400">暂无规格</div>
            ) : (
              specs.map((spec) => {
                const isActive = !unlimitedSelected && (specId ? spec.id === specId : spec.name === currentModel);
                return (
                  <button
                    key={spec.id}
                    type="button"
                    onClick={() => {
                      onChange(spec.id, spec.price, spec.name, spec.unit);
                      setOpen(false);
                    }}
                    className={`w-full text-left flex items-start gap-2 px-3 py-2 transition-colors ${
                      isActive
                        ? 'bg-primary-50 text-primary-700 hover:bg-primary-50'
                        : 'text-foreground-700 hover:bg-background-50'
                    }`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <p className="text-xs font-medium truncate">{spec.name}</p>
                        {spec.isOutside && (
                          <span className="px-1.5 py-0.5 bg-orange-500 text-white text-[10px] rounded font-medium shrink-0">
                            清单外
                          </span>
                        )}
                      </div>
                      <p className="text-[10px] text-foreground-500">
                        {`¥${spec.price.toFixed(2)} / ${spec.unit}`}
                      </p>
                    </div>
                    {isActive && <i className="ri-check-line text-primary-500 ml-auto shrink-0" />}
                  </button>
                );
              })
            )}
          </div>,
          document.body,
        )}
    </>
  );
}

export default function OrderItemsTable({
  items,
  editMode,
  onUpdateQuantity,
  onUpdateSpec,
  onUpdateProduct,
  onUpdateRemark,
  onDeleteItem,
  lastItemError,
  headerLeft,
  headerActions,
  hideTitle = false,
  footerExtra,
  allowUnlimitedSpec = false,
  showPlanUsage = false,
  showOutsideColumn = false,
  showHospitalPlan = false,
  hospitalPlanQuantityOf,
}: OrderItemsTableProps) {
  const totalAmount = items.reduce((sum, item) => {
    if (isEmptyOrderItem(item)) return sum;
    return sum + item.unitPrice * item.quantity;
  }, 0);
  const totalQuantity = items.reduce((sum, item) => {
    if (isEmptyOrderItem(item)) return sum;
    return sum + (Number(item.quantity) || 0);
  }, 0);
  const colsBeforeQty =
    5 + (showPlanUsage ? 1 : 0) + (showOutsideColumn ? 1 : 0);

  return (
    <div className="bg-white rounded-xl border border-background-200 p-5">
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap min-w-0">
          {headerLeft}
          {!hideTitle && (
            <h3 className="text-sm font-semibold text-foreground-900 whitespace-nowrap">采购清单</h3>
          )}
        </div>
        {headerActions && <div className="flex items-center gap-2 shrink-0">{headerActions}</div>}
      </div>

      {editMode && (
        <div className="mb-3 px-3 py-2 bg-accent-50 border border-accent-200 rounded-lg text-xs text-accent-800 flex items-center gap-1.5">
          <i className="ri-information-line" />
          点击<strong>物资名称</strong>可按规格选择；可修改<strong>规格</strong>、<strong>数量</strong>、<strong>备注</strong>
          ，完成后点击「保存修改」
        </div>
      )}

      {lastItemError && (
        <div className="mb-3 px-3 py-2 bg-primary-50 border border-primary-200 rounded-lg text-xs text-primary-700 flex items-center gap-1.5">
          <i className="ri-error-warning-line" />
          采购清单至少需要保留一件物资
        </div>
      )}

      <div className="overflow-x-auto overflow-y-visible">
        <table className="w-full text-sm border-collapse [&_th]:border [&_th]:border-background-200 [&_td]:border [&_td]:border-background-100">
          <thead>
            <tr className="bg-[#f8f8f9] border-b border-background-200">
              <th className="text-center py-2.5 px-2 text-xs font-semibold text-foreground-600 w-10">序号</th>
              <th className="text-center py-2.5 px-2 text-xs font-semibold text-foreground-600 w-14">图片</th>
              <th className="text-left py-2.5 px-2 text-xs font-semibold text-foreground-600">
                物资名称
                {editMode && <span className="ml-1 text-accent-600 font-normal">可修改</span>}
              </th>
              <th className="text-left py-2.5 px-2 text-xs font-semibold text-foreground-600 w-[180px]">
                规格型号
                {editMode && <span className="ml-1 text-accent-600 font-normal">可修改</span>}
              </th>
              {showPlanUsage && (
                <th className="text-center py-2.5 px-2 text-xs font-semibold text-foreground-600 w-28 whitespace-nowrap">
                  计划已使用
                </th>
              )}
              {showOutsideColumn && (
                <th className="text-center py-2.5 px-2 text-xs font-semibold text-foreground-600 w-20 whitespace-nowrap">
                  清单外
                </th>
              )}
              <th className="text-center py-2.5 px-2 text-xs font-semibold text-foreground-600 w-16">单位</th>
              <th className="text-center py-2.5 px-2 text-xs font-semibold text-foreground-600 w-24">
                {showHospitalPlan ? '科室计划数' : '数量'}
                {editMode && <span className="ml-1 text-accent-600 font-normal">可修改</span>}
              </th>
              {showHospitalPlan && (
                <th className="text-center py-2.5 px-2 text-xs font-semibold text-foreground-600 w-24 whitespace-nowrap">
                  全院计划数
                </th>
              )}
              <th className="text-right py-2.5 px-2 text-xs font-semibold text-foreground-600 w-24">单价(元)</th>
              <th className="text-left py-2.5 px-2 text-xs font-semibold text-foreground-600 min-w-[140px]">
                物资备注
                {editMode && <span className="ml-1 text-accent-600 font-normal">可修改</span>}
              </th>
              <th className="text-right py-2.5 px-2 text-xs font-semibold text-foreground-600 w-24">小计(元)</th>
              {editMode && (
                <th className="text-center py-2.5 px-2 text-xs font-semibold text-foreground-600 w-16">操作</th>
              )}
            </tr>
          </thead>
          <tbody>
            {items.map((item, idx) => {
              const empty = isEmptyOrderItem(item);
              const img = item.image || '';
              const model = item.spec || (empty ? '' : '-');
              const remark = item.itemRemark || '';
              const showPlan =
                showPlanUsage &&
                item.planUsedQuantity != null &&
                item.planQuantity != null;
              const hospitalQty = showHospitalPlan && !empty && hospitalPlanQuantityOf
                ? hospitalPlanQuantityOf(item)
                : 0;
              const qtyOverflow =
                showHospitalPlan && !empty && Number(item.quantity) > hospitalQty;
              return (
                <tr
                  key={idx}
                  className={`transition-colors ${
                    qtyOverflow ? 'bg-red-50/70 hover:bg-red-50' : 'hover:bg-background-50/60'
                  }`}
                >
                  <td className="py-2.5 px-2 text-xs text-foreground-400 text-center">{idx + 1}</td>
                  <td className="py-2 px-2 text-center">
                    <div className="w-10 h-10 mx-auto rounded-md overflow-hidden bg-background-100 shrink-0">
                      {img ? (
                        <img
                          src={img}
                          alt={item.name}
                          className="w-full h-full object-cover object-top"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <i className="ri-image-line text-foreground-300 text-lg" />
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="py-2.5 px-2">
                    {editMode ? (
                      <ProductNameCell
                        name={item.name}
                        currentSpecId={item.specId}
                        onPick={(payload) => onUpdateProduct(idx, payload)}
                      />
                    ) : (
                      <span className="text-sm font-medium text-foreground-900">{item.name}</span>
                    )}
                  </td>
                  <td className="py-2.5 px-2">
                    {editMode ? (
                      <SpecCell
                        productId={item.productId}
                        specId={item.specId}
                        currentModel={model}
                        options={item.specOptions}
                        allowUnlimitedSpec={allowUnlimitedSpec}
                        onChange={(sid, price, m, unit) => onUpdateSpec(idx, sid, price, m, unit)}
                      />
                    ) : (
                      <span className="text-xs text-foreground-600">{model}</span>
                    )}
                  </td>
                  {showPlanUsage && (
                    <td className="py-2.5 px-2 text-center text-xs tabular-nums whitespace-nowrap">
                      {showPlan ? (
                        <span
                          title={
                            item.planMultipleUnit != null && item.planMultipleUnit > 1
                              ? `已使用量 / 计划量（最小单位 ${formatPlanQuantity(item.planBaseUsedQuantity)} / ${formatPlanQuantity(item.planBaseQuantity)}）`
                              : '已使用量 / 计划量'
                          }
                        >
                          <span
                            className={
                              item.planQuantity! > 0 &&
                              item.planUsedQuantity! >= item.planQuantity!
                                ? 'text-red-600 font-medium'
                                : 'text-foreground-700'
                            }
                          >
                            {formatPlanQuantity(item.planUsedQuantity)}
                          </span>
                          <span className="text-foreground-400 mx-0.5">/</span>
                          <span className="text-foreground-700">
                            {formatPlanQuantity(item.planQuantity)}
                          </span>
                        </span>
                      ) : (
                        <span className="text-foreground-400">-</span>
                      )}
                    </td>
                  )}
                  {showOutsideColumn && (
                    <td className="py-2.5 px-2 text-center">
                      {!empty && item.isOutside ? (
                        <span className="inline-flex px-1.5 py-0.5 bg-orange-500 text-white text-[10px] rounded font-medium whitespace-nowrap">
                          清单外
                        </span>
                      ) : (
                        <span className="text-xs text-foreground-400">{empty ? '-' : ''}</span>
                      )}
                    </td>
                  )}
                  <td className="py-2.5 px-2 text-xs text-foreground-600 text-center">
                    {empty ? '-' : item.unit}
                  </td>
                  <td className="py-2.5 px-2 text-center">
                    {editMode ? (
                      <QuantityCell
                        value={item.quantity}
                        onChange={(v) => onUpdateQuantity(idx, v)}
                        disabled={empty}
                      />
                    ) : (
                      <span className={`text-sm ${qtyOverflow ? 'text-red-600 font-medium' : 'text-foreground-700'}`}>
                        {item.quantity}
                      </span>
                    )}
                  </td>
                  {showHospitalPlan && (
                    <td
                      className={`py-2.5 px-2 text-center text-xs whitespace-nowrap ${
                        empty
                          ? 'text-foreground-400'
                          : qtyOverflow
                            ? 'text-red-600 font-medium'
                            : 'text-foreground-600'
                      }`}
                      title={qtyOverflow ? '科室计划数不能大于全院计划数' : undefined}
                    >
                      {empty ? '-' : hospitalQty}
                    </td>
                  )}
                  <td className="py-2.5 px-2 text-right text-xs text-foreground-600">
                    {empty ? '-' : `¥${item.unitPrice.toFixed(2)}`}
                  </td>
                  <td className="py-2.5 px-2">
                    {editMode ? (
                      <RemarkCell
                        value={remark}
                        onChange={(v) => onUpdateRemark(idx, v)}
                        disabled={empty}
                      />
                    ) : (
                      <span className="text-xs text-foreground-600 break-all">
                        {remark || '-'}
                      </span>
                    )}
                  </td>
                  <td className="py-2.5 px-2 text-right text-sm font-medium text-foreground-900">
                    {empty ? '-' : `¥${(item.unitPrice * item.quantity).toFixed(2)}`}
                  </td>
                  {editMode && (
                    <td className="py-2.5 px-2 text-center">
                      <button
                        type="button"
                        onClick={() => onDeleteItem(idx)}
                        className="w-7 h-7 flex items-center justify-center rounded-md text-foreground-400 hover:text-primary-600 hover:bg-primary-50 transition-colors mx-auto"
                        title="删除"
                      >
                        <i className="ri-delete-bin-line" />
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="bg-background-50">
              <td
                colSpan={colsBeforeQty}
                className="py-3 px-2 text-right text-sm text-foreground-500"
              >
                采购数量合计
              </td>
              <td className="py-3 px-2 text-center text-lg font-bold text-foreground-900 tabular-nums">
                {totalQuantity}
              </td>
              {showHospitalPlan && <td />}
              <td />
              <td className="py-3 px-2 text-right text-sm text-foreground-500">采购总额</td>
              <td className="py-3 px-2 text-right text-lg font-bold text-primary-600">
                ¥{totalAmount.toFixed(2)}
              </td>
              {editMode && <td />}
            </tr>
          </tfoot>
        </table>
      </div>

      {footerExtra && <div className="mt-4 pt-4 border-t border-background-200">{footerExtra}</div>}
    </div>
  );
}
